import GenericContainer from '../../components/@container/generic-container';
import Layout from '../../components/layout';
import React, { useState } from 'react';
import InputField from '../../components/input-field';
import Button from '../../components/button';
import useAddAllToCart from '../../hooks/use-add-all-to-cart';
import useVinDecoder from '../../hooks/use-vin-decoder';
import fetchData from '../../api/fetch-data';
import useCustomer from '../../hooks/use-customer';
import AccountPrompt from '../../components/account-prompt';

const KeycodesPage = () => {
  const customer = useCustomer();

  const addToCart = useAddAllToCart({
    onCompleted: () => {
      window.location.href = '/cart';
    },
  });
  const [vspError, setVspError] = useState<string | null>(null);
  const [passCodeError, setPasscodeError] = useState<string | null>(null);
  const [vinError, setVinError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vsp, setVsp] = useState<string>();
  const [passCode, setPasscode] = useState<string>();
  const [vin, setVin] = useState<string>();
  const vinDecoder = useVinDecoder();

  if (!customer) return null;

  return (
    <Layout title="Key Codes">
      <GenericContainer
        title="Key Codes"
        breadcrumb={{ url: '/', text: 'Back to Home' }}
      >
        <div>
          <p>
            Registered Vehicle Security Professionals* may obtain key cutting
            codes for any U.S. or Canada Volvo car model from model year 1997
            and onward. Key cutting codes, which can be programmed into
            commercially available key cutting machines, allow a replacement key
            to be cut even when a duplicate or master key is not available for
            copying.
          </p>

          <p>
            A "Registered Vehicle Security Professional" includes anyone who is
            a member, in good standing, of the NASTF Vehicle Security Registry.
          </p>

          <p>
            If you are a locksmith or other vehicle security professional but
            not already a member of the NASTF Vehicle Security Registry, go to
            www.nastf.org/vehiclesecurityinformation for more information and a
            registry membership application.
          </p>

          <p>
            For help with your vehicle security credential please contact the
            NASTF Vehicle Security Professional help desk at 810-289-4801 or
            email support@nastfsecurityregistry.org.
          </p>
          <h2>How To Obtain A Key Code</h2>
          <p>
            Enter a valid Vehicle Security Credential (VSC), passcode, and
            Vehicle Identification Number. Too many attempts with incorrect
            credentials will result in account lockout. If this happens, your
            company VSP administrator will receive an email with a temporary
            password that allows them to use the Registry to set a new,
            confidential password for you.
          </p>
          <p>
            If you do not remember your passcode, contact your company's VSP
            administrator. They can reset your passcode for you.
          </p>
          <p>
            By clicking "Accept/Continue" below, I signify that I have complied
            with the Terms of Use for this service.
          </p>
          <div className="spacer" />
          {customer.customer!.guest ? (
            <AccountPrompt text="Please sign in or create an account in order to proceed." />
          ) : (
            <div>
              <InputField
                id="vspid"
                label="VSP ID"
                type="text"
                defaultValue={''}
                onChange={({ target: { value } }) => {
                  setVspError(null);
                  setError(null);
                  setVsp(value);
                }}
                error={vspError}
                cy="vsp-input"
              />
              <InputField
                cy="passcode-input"
                id="passcode"
                label="Passcode"
                type="text"
                defaultValue={''}
                onChange={({ target: { value } }) => {
                  setPasscodeError(null);
                  setError(null);
                  setPasscode(value);
                }}
                error={passCodeError}
              />
              <InputField
                cy="vin-input"
                id="vin"
                label="VIN"
                type="text"
                defaultValue={''}
                onChange={({ target: { value } }) => {
                  setVinError(null);
                  setError(null);
                  setVin(value);
                }}
                error={vinError}
              />
              {error && <div className="error">{error}</div>}
              <Button
                cy="submit"
                text="Submit"
                onClick={async () => {
                  if (!vsp || !passCode || !vin) {
                    if (!vsp) {
                      setVspError('required');
                    } else {
                      setVspError(null);
                    }
                    if (!passCode) {
                      setPasscodeError('required');
                    } else {
                      setPasscodeError(null);
                    }
                    if (!vin) {
                      setVinError('required');
                    } else {
                      setVinError(null);
                    }

                    return;
                  }

                  if (
                    !(
                      vin.toLowerCase().startsWith('yv1') ||
                      vin.toLowerCase().startsWith('yv4') ||
                      vin.toLowerCase().startsWith('lvy') ||
                      vin.toLowerCase().startsWith('7jr') ||
                      vin.toLowerCase().startsWith('lyv') ||
                      vin.toLowerCase().startsWith('7jd')
                    )
                  ) {
                    setVinError('This is not a Volvo VIN Number.');
                    return;
                  }

                  const page = await fetchData({
                    type: 'keycode_verify',
                    jsonInput: JSON.stringify({
                      vspid: vsp,
                      passCode,
                      vin,
                    }),
                    page: 1,
                    size: 1,
                  });

                  const verify = JSON.parse(page.content[0].json);

                  if (verify.message) {
                    setError(verify.message);
                    return;
                  }

                  const data = await vinDecoder(vin as string);

                  if (data.errors.length) {
                    setVinError('invalid vin');
                  } else {
                    addToCart([
                      {
                        productId: 'A1698AFC-D406-4D90-885C-4347A27CE14E',
                        quantity: 1,
                        format: 'software',
                        productAttributes: [
                          {
                            code: 'id',
                            value: verify.id,
                          },
                          {
                            code: 'vin',
                            value: vin,
                          },
                        ],
                      },
                    ]);
                  }
                }}
              />
            </div>
          )}
        </div>
      </GenericContainer>
      {/* language=CSS */}
      <style jsx>
        {`
          p,
          h2 {
            font-family: 'Volvo Novum', sans-serif;
          }

          p {
            font-size: 16px;
          }

          h2 {
            font-weight: 400;
          }

          .spacer {
            height: 20px;
          }

          .error {
            font-family: 'Volvo Novum', sans-serif;
            color: red;
          }
        `}
      </style>
    </Layout>
  );
};

export default KeycodesPage;
